import { styled } from "@linaria/react"
import { observer } from "mobx-react-lite"
import { useRef } from "react"

import { LoadMoreButton } from "@/components/Chat/LoadMoreButton.tsx"
import { Gap, Horizontal, Spring } from "@/components/Flex.ts"
import { createLLMLogPagesModel } from "@/components/LLMLogChat/LLMLogPagesModel.ts"
import { Spinner } from "@/components/Spinner/Spinner.tsx"
import { Tooltip } from "@/components/Tooltip/Tooltip.tsx"
import { useModel } from "@/hooks/useModel.ts"
import { useQueries } from "@/hooks/useRootContext.ts"
import { HelpLight } from "@/icons/Icons.ts"
import { formatDateTime } from "@/lib/date.ts"
import * as textStyles from "@/styles/textStyles.ts"
import { ellipsize } from "@/styles/textStyles.ts"

import type { ScrollableModel } from "@/components/Scrollable/ScrollableModel.ts"
import type { LLMLogPage } from "@/lib/client/generated/TypeDefinitions.ts"
import type { TaskViewModel } from "@/routes/tasks/TaskViewScene/TaskViewModel.ts"

export const LLMLogPagesList = observer((props: {
  model: TaskViewModel
  scrollableModel: ScrollableModel
  onPageSelect: (pageId: string) => void
  selectedPageId: string | null
}) => {
  const task = props.model.task.get()
  const taskId = task?.id
  const queries = useQueries()

  const loadMorePressed = useRef(false)

  const pagesModel = useModel(() => {
    return taskId ? createLLMLogPagesModel(taskId, queries) : null
  }, [taskId])

  if (!pagesModel) {
    return null
  }

  return (
    <Container>
      <Horizontal alignItems="baseline">
        {pagesModel.isReady.get()
          ? <LoadMoreButton model={pagesModel} loadNext={false} loadMorePressed={loadMorePressed} />
          : <Spinner text={ellipsize("Loading pages")} />
        }

        <Spring />

        <Tooltip align="end" content={
          <HelpText>
            Select a page to view its LLM log entries. Pages group related log entries together.
          </HelpText>
        }>
          <HelpLight color="var(--secondary-text-color)" />
        </Tooltip>
      </Horizontal>

      <Gap height="8px" />

      {pagesModel.pages.get().map(page => (
        <PageItem
          key={page.id}
          page={page}
          isSelected={props.selectedPageId === page.id}
          onClick={() => props.onPageSelect(page.id)}
        />
      ))}

      <LoadMoreButton model={pagesModel} loadNext loadMorePressed={loadMorePressed} />
    </Container>
  )
})

const PageItem = observer((props: {
  page: LLMLogPage
  isSelected: boolean
  onClick: () => void
}) => {
  const time = formatDateTime(props.page.createdAt)

  return (
    <PageItemContainer
      isSelected={props.isSelected}
      onClick={props.onClick}
    >
      <PageItemContent>
        <PageItemLabel>{props.page.label}</PageItemLabel>
        <PageItemTime>{time}</PageItemTime>
      </PageItemContent>
    </PageItemContainer>
  )
})

const Container = styled.div`
  width: 100%;
`

const PageItemContainer = styled.div<{ isSelected: boolean }>`
  padding: 12px;
  margin-bottom: 8px;
  border-radius: var(--border-radius-6);
  border: 1px solid ${props => props.isSelected ? "var(--main-color)" : "var(--text-color-10)"};
  background-color: ${props => props.isSelected ? "var(--main-color-5)" : "transparent"};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: ${props => props.isSelected ? "var(--main-color-10)" : "var(--text-color-5)"};
    border-color: ${props => props.isSelected ? "var(--main-color)" : "var(--text-color-20)"};
  }
`

const PageItemContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const PageItemLabel = styled.div`
  ${textStyles.baseFontSize};
  font-weight: 500;
  color: var(--text-color);
`

const PageItemTime = styled.div`
  ${textStyles.smallFontSize};
  color: var(--text-color-40);
`


const HelpText = styled.div`
  max-width: 320px;
`

